import { faEyeSlash } from '@fortawesome/free-regular-svg-icons'
import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Authorization from '../../Authentication/Authorization'
import DialogContext from '../../Authentication/DialogContext'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import { FormDataControl } from '../../components/FormFields'
import ProtectedButton from '../../components/ProtectedButton'
import Loading from '../../layout/Loading'
import KeyManagementService from '../../service/KeyManagementService'
import GatewayType from '../GatewayType'

const EncryptionKeys: GatewayType = () => {
  const { t } = useTranslation()
  const [keys, setKeys] = useState([] as any[])
  const [loading, setLoading] = useState(true)
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    async function loadData() {
      await KeyManagementService.getKeys(setKeys)
    }
    loadData().finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: t('keys.name'),
        dataField: 'name',
        sort: true,
      },
      {
        text: t('keys.keyId'),
        dataField: 'keyId',
        sort: true,
      },
      {
        text: t('keys.algorithm'),
        dataField: 'algorithm',
        sort: true,
      },
      {
        text: t('keys.version'),
        dataField: 'keyVersion',
        sort: true,
      },
      {
        text: t('action'),
        dataField: 'df1',
        isDummyField: true,
        headerAlign: 'center',
        formatter: (cellData: any, row: any) => {
          return (
            <>
              <ProtectedButton
                permissions={Authorization.keystore.keys.edit}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('edit')}
                onClick={() => editLunaHsmKey(row, setKeys, t, editDialogPropsSetter)}
                shouldRender={row.hsmType === 'Luna'}>
                <FontAwesomeIcon icon={faEdit} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.keystore.keys.edit}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('edit')}
                onClick={() => editUtimacoHsmKey(row, setKeys, t, editDialogPropsSetter)}
                shouldRender={row.hsmType === 'Utimaco'}>
                <FontAwesomeIcon icon={faEdit} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.keystore.keys.delete}
                variant='link'
                className='text-danger text-link text-decoration-none'
                tooltip={t('hide')}
                onClick={() => deleteKeys(row, setKeys, t, confirmDialogPropsSetter)}>
                <FontAwesomeIcon icon={faEyeSlash} />
              </ProtectedButton>
            </>
          )
        },
      },
    ]
  }, [t, confirmDialogPropsSetter, editDialogPropsSetter])
  return (
    <Loading isLoading={loading}>
      <DataTable
        data={keys}
        columns={columns}
        caption={t('keys.encryptionKeys')}
        captionGridSize={5}
        captionButtons={[
          {
            onClick: () => createKeys(),
            text: t('new'),
            permissions: Authorization.keystore.keys.edit,
          },
        ]}
        helpLink={t('keys.helpLink')}
      />
    </Loading>
  )

  function createKeys() {
    const editDialogProps: EditDialogProps = {
      show: true,
      header: t('keys.createKeys'),
      onSubmit: KeyManagementService.createKeys,
      onSubmitExtraArgs: [setKeys, t],
      data: (custom: string[]) => [
        {
          id: 'mode',
          type: 'select',
          options: ['Random Keys', 'Luna HSM', 'Utimaco HSM'],
          onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value]),
          value: custom[0],
          label: t('type.text'),
          required: true,
        },
        ...getSubKeyDefinitions(custom[0]),
      ],
      initCustom: ['Random Keys'],
      labelGridSize: 4,
    }

    editDialogPropsSetter(editDialogProps)
  }

  function getSubKeyDefinitions(custom: string): FormDataControl[] {
    if (custom === 'Random Keys') {
      return [
        {
          id: 'label',
          type: 'label',
          label: t('keys.createRandomKeysBody'),
        },
      ]
    } else if (custom === 'Luna HSM') {
      return [
        {
          id: 'name',
          type: 'text',
          label: t('name.text'),
          required: true,
          requiredMessage: t('name.inValidMessage'),
        },
        {
          id: 'keyAlias',
          type: 'text',
          label: t('keys.keyAlias'),
          required: true,
          requiredMessage: t('invalidMessage', { label: 'Key Alias' }),
        },
        {
          id: 'partition',
          type: 'text',
          label: t('keys.partition'),
          required: true,
          requiredMessage: t('invalidMessage', { label: 'Partition' }),
        },
        {
          id: 'password',
          type: 'password',
          autoComplete: 'new-password',
          label: t('password.text'),
          required: true,
          requiredMessage: t('password.inValidMessage'),
        },
      ]
    } else if (custom === 'Utimaco HSM') {
      return [
        {
          id: 'name',
          type: 'text',
          label: t('name.text'),
          required: true,
          requiredMessage: t('name.inValidMessage'),
        },
        {
          id: 'device',
          type: 'text',
          label: t('keys.device'),
          required: true,
          requiredMessage: t('invalidMessage', { label: 'device' }),
        },
        {
          id: 'defaultUser',
          type: 'text',
          label: t('keys.defaultUser'),
          required: true,
          requiredMessage: t('invalidMessage', { label: 'default user' }),
        },
        {
          id: 'password',
          type: 'password',
          autoComplete: 'new-password',
          label: t('password.text'),
          required: true,
          requiredMessage: t('password.inValidMessage'),
        },
        {
          id: 'keyName',
          type: 'text',
          label: t('keys.keyName'),
          required: true,
          requiredMessage: t('invalidMessage', { label: 'key name' }),
        },
        {
          id: 'keyGroup',
          type: 'text',
          label: t('keys.keyGroup'),
        },
        {
          id: 'keySpecifier',
          type: 'text',
          label: t('keys.keySpecifier'),
        },
      ]
    }
    return []
  }
}

function editLunaHsmKey(row: any, setKeys: React.Dispatch<any>, t: TFunction, editDialogPropsSetter: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('keys.editLunaHeader'),
    onSubmit: KeyManagementService.editLunaHsmKey,
    onSubmitExtraArgs: [setKeys, t],
    data: [
      {
        id: 'id',
        type: 'hidden',
        value: row.id,
        render: false,
      },
      {
        id: 'keyId',
        type: 'hidden',
        value: row.keyId,
        render: false,
      },
      {
        id: 'keyVersion',
        type: 'hidden',
        value: row.keyVersion,
        render: false,
      },
      {
        id: 'shortKeyId',
        type: 'hidden',
        value: row.shortKeyId,
        render: false,
      },
      {
        id: 'name',
        type: 'text',
        label: t('name.text'),
        value: row.name,
        required: true,
        requiredMessage: t('name.inValidMessage'),
      },
      {
        id: 'keyAlias',
        type: 'text',
        label: t('keys.keyAlias'),
        value: JSON.parse(row.keyInfo.keyInfoString).keyAlias,
        required: true,
        requiredMessage: t('invalidMessage', { label: 'Key Alias' }),
      },
      {
        id: 'partition',
        type: 'text',
        label: t('keys.partition'),
        value: JSON.parse(row.keyInfo.keyInfoString).partition,
        required: true,
        requiredMessage: t('invalidMessage', { label: 'Partition' }),
      },
      {
        id: 'password',
        type: 'password',
        autoComplete: 'new-password',
        label: t('password.text'),
        value: JSON.parse(row.keyInfo.keyInfoString).password,
        required: true,
        requiredMessage: t('password.inValidMessage'),
      },
    ],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function editUtimacoHsmKey(row: any, setKeys: React.Dispatch<any>, t: TFunction, editDialogPropsSetter: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('keys.editLunaHeader'),
    onSubmit: KeyManagementService.editUtimacoHsmKey,
    onSubmitExtraArgs: [setKeys, t],
    data: [
      {
        id: 'id',
        type: 'hidden',
        value: row.id,
        render: false,
      },
      {
        id: 'keyId',
        type: 'hidden',
        value: row.keyId,
        render: false,
      },
      {
        id: 'keyVersion',
        type: 'hidden',
        value: row.keyVersion,
        render: false,
      },
      {
        id: 'shortKeyId',
        type: 'hidden',
        value: row.shortKeyId,
        render: false,
      },
      {
        id: 'name',
        type: 'text',
        label: t('name.text'),
        value: row.name,
        required: true,
        requiredMessage: t('name.inValidMessage'),
      },
      {
        id: 'device',
        type: 'text',
        label: t('keys.device'),
        value: JSON.parse(row.keyInfo.keyInfoString).device,
        required: true,
        requiredMessage: t('invalidMessage', { label: 'device' }),
      },
      {
        id: 'defaultUser',
        type: 'text',
        label: t('keys.defaultUser'),
        value: JSON.parse(row.keyInfo.keyInfoString).defaultUser,
        required: true,
        requiredMessage: t('invalidMessage', { label: 'default user' }),
      },
      {
        id: 'password',
        type: 'password',
        autoComplete: 'new-password',
        label: t('password.text'),
        value: JSON.parse(row.keyInfo.keyInfoString).password,
        required: true,
        requiredMessage: t('password.inValidMessage'),
      },
      {
        id: 'keyName',
        type: 'text',
        label: t('keys.keyName'),
        value: JSON.parse(row.keyInfo.keyInfoString).keyName,
        required: true,
        requiredMessage: t('invalidMessage', { label: 'key name' }),
      },
      {
        id: 'keyGroup',
        type: 'text',
        label: t('keys.keyGroup'),
        value: JSON.parse(row.keyInfo.keyInfoString).keyGroup,
        required: true,
        requiredMessage: t('invalidMessage', { label: 'key group' }),
      },
      {
        id: 'keySpecifier',
        type: 'text',
        label: t('keys.keySpecifier'),
        value: JSON.parse(row.keyInfo.keyInfoString).keySpecifier,
        required: true,
        requiredMessage: t('invalidMessage', { label: 'key specifier' }),
      },
    ],
    labelGridSize: 4,
  }

  editDialogPropsSetter(editDialogProps)
}

function deleteKeys(row: any, setKeys: React.Dispatch<any>, t: TFunction, confirmDialogPropsSetter: any) {
  if (row.hsmType === 'Luna' || row.hsmType === 'Utimaco') {
    hideHsmKeys(row, setKeys, t, confirmDialogPropsSetter)
  } else {
    deleteLegacyKeys(row, setKeys, t, confirmDialogPropsSetter)
  }
}

function deleteLegacyKeys(row: any, setKeys: React.Dispatch<any>, t: TFunction, confirmDialogPropsSetter: any) {
  const editDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('keys.hideLegacyHeader'),
    body: (
      <>
        {t('keys.hideLegacyBody')
          .split('\n')
          .map((item) => (
            <p>{item}</p>
          ))}
      </>
    ),
    submitText: 'Hide',
    onSubmit: KeyManagementService.hideLegacyKey,
    onSubmitExtraArgs: [row.keyId, row.keyVersion, setKeys, t],
  }
  confirmDialogPropsSetter(editDialogProps)
}

function hideHsmKeys(row: any, setKeys: React.Dispatch<any>, t: TFunction, confirmDialogPropsSetter: any) {
  const editDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('keys.hideHsmHeader'),
    body: (
      <>
        {t('keys.hideHsmBody')
          .split('\n')
          .map((item) => (
            <p>{item}</p>
          ))}
      </>
    ),
    submitText: 'Hide',
    onSubmit: KeyManagementService.hideHsmKey,
    onSubmitExtraArgs: [row.keyId, row.keyVersion, setKeys, t],
  }

  confirmDialogPropsSetter(editDialogProps)
}

export default EncryptionKeys
